import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import { effectiveParams, paramStatusFor, paramDeltaLabel } from '../params.js';
import { getDisplayUnits } from '../units.js';
import { readSalinityUnit } from '../salinity.js';
import { assessFreshnessClient, currentReadingsClient, relativeTimeShort } from '../utils.js';
import { isCycleQuiet, isGradedParam } from '../../../shared/tank-purpose.js';
import { ParamHistoryChart } from './ParamHistoryChart.jsx';
import {
  historyPill,
  paramToDisplay,
  pointToDisplay,
  toDisplayValue,
  displayUnit,
} from '../all-history.js';

// Every parameter's history on one page (feedback #60): one chart per
// card the keeper shows on Home, in their own order, each led by the
// answer (latest value, how far from target, how old) before the line.
// Tapping a chart opens that parameter's full history, where a reading
// can be deleted.
const RANGES = [
  { k: 7, label: '7d' },
  { k: 30, label: '30d' },
  { k: 90, label: '90d' },
  { k: 365, label: '1y' },
  { k: 0, label: 'All' },
];

const PILL_TONE = {
  OK: 'ok',
  HIGH: 'off',
  LOW: 'off',
  STALE: 'stale',
  CHECK: 'stale',
};

export function AllHistoryView({
  tank,
  onClose,
  onOpenParam,
  onOpenImport = null,
  initialRangeDays = 30,
}) {
  const [rangeDays, setRangeDays] = useState(initialRangeDays);
  const [series, setSeries] = useState(null);
  const [err, setErr] = useState('');
  const params = useMemo(() => effectiveParams(tank), [tank]);
  const opts = useMemo(
    () => ({ salinityUnit: readSalinityUnit(), tempUnit: getDisplayUnits(tank).temp }),
    [tank]
  );

  useEffect(() => {
    let alive = true;
    setErr('');
    const qs = rangeDays ? `?days=${rangeDays}` : '';
    api(`/api/tanks/${tank.id}/readings/overview${qs}`)
      .then((r) => {
        if (alive) setSeries(r?.series || {});
      })
      .catch((e) => {
        if (alive) setErr(e.message || 'Could not load the history.');
      });
    return () => {
      alive = false;
    };
  }, [tank.id, rangeDays]);

  // The latest reading per param comes from the tank payload (exact rows),
  // never from a bucketed median.
  const byParam = useMemo(() => {
    const m = {};
    for (const r of tank.readings || []) (m[r.param] ||= []).push(r);
    for (const list of Object.values(m)) list.sort((a, b) => new Date(b.ts) - new Date(a.ts));
    return m;
  }, [tank.readings]);

  return (
    <div className="ah" style={{ padding: '0 16px 32px' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 12px' }}>
        <button type="button" className="ph-back" onClick={onClose} aria-label="Back">
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ph-eyebrow">{tank.name}</div>
          <h2 className="font-heading ph-title">All history</h2>
        </div>
        {onOpenImport && (
          <button
            type="button"
            className="font-heading"
            onClick={onOpenImport}
            style={{
              minHeight: 44,
              padding: '0 14px',
              fontSize: 12,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              borderRadius: 999,
              border: '1px solid var(--surface-border)',
              background: 'transparent',
              color: 'var(--cyan)',
              cursor: 'pointer',
            }}
          >
            Import tests
          </button>
        )}
      </header>

      <div className="ph-range" role="toolbar" aria-label="Time range">
        {RANGES.map((opt) => (
          <button
            key={opt.label}
            type="button"
            className={`ph-range-btn ${rangeDays === opt.k ? 'active' : ''}`}
            onClick={() => setRangeDays(opt.k)}
            aria-pressed={rangeDays === opt.k}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>
        Tap a line to read a day. Slide along it to move.
      </p>

      {err && (
        <div role="alert" className="ts-err" style={{ margin: '10px 0' }}>
          {err}
        </div>
      )}
      {!series && !err && (
        <p style={{ color: 'var(--text-dim)', fontSize: 15 }}>Loading your readings…</p>
      )}

      {series &&
        params.map((p) => {
          const k = p.key;
          const readings = currentReadingsClient(byParam[k] || [], k);
          const latest = readings[0] || null;
          const st = latest ? paramStatusFor(tank, k, latest.value) : 'unknown';
          const freshness = assessFreshnessClient(readings, k);
          const stale = !!freshness?.stale && isGradedParam(tank, k);
          const pill = historyPill({
            hasReading: !!latest,
            stale,
            staleReason: freshness?.reason || null,
            st,
            cycling: isCycleQuiet(tank, k),
            value: latest?.value,
            band: p.acceptable,
          });
          // A logged-only test (a QT's salinity in hyposalinity, a fishless
          // cycle's ammonia) gets no shaded range: the reef band behind a
          // deliberate 16 ppt reads as an emergency.
          const graded = isGradedParam(tank, k);
          const shown = { ...paramToDisplay(k, p, opts), ...(graded ? {} : { acceptable: null }) };
          const points = (series[k]?.points || []).map((pt) => pointToDisplay(k, pt, opts));
          const delta =
            latest && st !== 'tracked' && st !== 'implausible' && !stale
              ? paramDeltaLabel(k, latest.value, p.target, {
                  displayUnits: { temp: opts.tempUnit },
                  salinityUnit: opts.salinityUnit,
                })
              : null;
          const bucket = series[k]?.bucket;
          return (
            <section
              key={k}
              className="slide-in"
              style={{
                border: '1px solid var(--surface-border)',
                borderRadius: 16,
                padding: 12,
                margin: '12px 0',
                background: 'var(--surface)',
              }}
            >
              <button
                type="button"
                onClick={() => onOpenParam(k)}
                style={{
                  all: 'unset',
                  display: 'block',
                  width: '100%',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
                    {p.label.toUpperCase()}
                  </span>
                  <span className={`hv-param-pill hv-param-pill-${PILL_TONE[pill] || 'tbd'}`}>
                    {pill}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span className="font-mono" style={{ fontSize: 17 }}>
                    {latest ? toDisplayValue(k, latest.value, opts) : '—'}
                    <span style={{ fontSize: 13, color: 'var(--text-dim)', marginLeft: 4 }}>
                      {displayUnit(k, p.unit, opts)}
                    </span>
                  </span>
                </div>
                <div
                  className="font-mono"
                  style={{ fontSize: 13, color: 'var(--text-dim)', margin: '4px 0 8px' }}
                >
                  {latest
                    ? [delta, relativeTimeShort(latest.ts)].filter(Boolean).join(' · ')
                    : 'No reading yet'}
                  {bucket ? ` · chart shows the middle reading per ${bucket}` : ''}
                  <span style={{ color: 'var(--cyan)' }}> · Open ›</span>
                </div>
              </button>
              {/* Not a tap target: a finger on the chart scrubs the line.
                  The header above opens the full history. */}
              {points.length < 2 ? (
                <div className="font-mono" style={{ fontSize: 13, color: 'var(--text-dim)' }}>
                  {points.length === 0
                    ? 'No readings in this range.'
                    : 'One reading in this range. A line needs two.'}
                </div>
              ) : (
                <div>
                  <ParamHistoryChart
                    readings={points}
                    param={shown}
                    status={pill === 'HIGH' || pill === 'LOW' ? 'off' : 'ok'}
                    rangeDays={rangeDays}
                    compact
                  />
                </div>
              )}
            </section>
          );
        })}
    </div>
  );
}
